"use client";

import { useEffect, useRef, useState } from "react";
import { Camera, Mic, Check, Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { uploadMedia } from "@/lib/interview/api";
import { useDict } from "@/lib/i18n/context";

type Step = "photo" | "recording" | "review" | "uploading" | "done";

function pickRecorderMime(): string | undefined {
  const candidates = [
    "video/webm;codecs=vp8,opus",
    "video/webm;codecs=vp9,opus",
    "video/webm",
  ];
  for (const mime of candidates) {
    if (
      typeof MediaRecorder !== "undefined" &&
      MediaRecorder.isTypeSupported(mime)
    ) {
      return mime;
    }
  }
  return undefined;
}

export function IdentityCapture({
  token,
  stream,
  videoRef,
  onDone,
}: {
  token: string;
  stream: MediaStream;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  onDone: () => void;
}) {
  const t = useDict();
  const [step, setStep] = useState<Step>("photo");
  const [seconds, setSeconds] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const blobRef = useRef<Blob | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);

  function revokePreview() {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
    blobRef.current = null;
  }

  useEffect(() => {
    if (step === "review") return;
    const el = videoRef.current;
    if (!el) return;
    if (el.srcObject !== stream) {
      el.srcObject = stream;
    }
    el.play().catch(() => {});
  }, [stream, videoRef, step]);

  useEffect(() => {
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      try {
        if (recorderRef.current?.state === "recording") {
          recorderRef.current.stop();
        }
      } catch {
        /* ignore */
      }
      revokePreview();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cleanup only on unmount
  }, []);

  async function capturePhoto() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((r) =>
      canvas.toBlob(r, "image/jpeg", 0.8),
    );
    if (blob) await uploadMedia(token, "identity_photo", blob).catch(() => {});
    startRecording();
  }

  function startRecording() {
    revokePreview();
    setStep("recording");
    setSeconds(0);

    const mime = pickRecorderMime();
    const recorder = mime
      ? new MediaRecorder(stream, { mimeType: mime })
      : new MediaRecorder(stream);
    recorderRef.current = recorder;
    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
    recorder.onstop = () => {
      recorderRef.current = null;
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
      const type = mime?.split(";")[0] || "video/webm";
      const blob = new Blob(chunks, { type });
      blobRef.current = blob;
      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      setPreviewUrl(url);
      setStep("review");
    };
    recorder.start();

    const started = Date.now();
    tickRef.current = setInterval(() => {
      const s = Math.floor((Date.now() - started) / 1000);
      setSeconds(s);
      if (s >= 5) {
        if (tickRef.current) {
          clearInterval(tickRef.current);
          tickRef.current = null;
        }
        if (recorder.state === "recording") recorder.stop();
      }
    }, 250);
  }

  function retake() {
    startRecording();
  }

  async function confirmAndContinue() {
    const blob = blobRef.current;
    if (!blob) return;
    setStep("uploading");
    await uploadMedia(token, "identity_voice", blob).catch(() => {});
    revokePreview();
    setStep("done");
    setTimeout(onDone, 400);
  }

  const showLive = step === "photo" || step === "recording";
  const showPreview = step === "review" && previewUrl;

  return (
    <div className="mx-auto max-w-xl animate-fade-in">
      <Card>
        <CardContent className="space-y-5 pt-6 text-center">
          <div>
            <h1 className="text-xl font-bold">{t.identity.title}</h1>
            <p className="text-sm text-muted">
              {step === "photo" && t.identity.capturePrompt}
              {step === "recording" && t.identity.recordingPrompt}
              {step === "review" && t.identity.reviewPrompt}
              {step === "uploading" && t.identity.saving}
              {step === "done" && t.identity.allSet}
            </p>
          </div>

          <div className="relative mx-auto aspect-video w-full overflow-hidden rounded-2xl bg-slate-900">
            <video
              ref={videoRef}
              muted
              playsInline
              className={
                showLive
                  ? "h-full w-full object-cover"
                  : "pointer-events-none absolute h-0 w-0 opacity-0"
              }
            />
            {showPreview && (
              <video
                key={previewUrl}
                src={previewUrl}
                controls
                playsInline
                className="h-full w-full object-cover"
              />
            )}
            {(step === "uploading" || step === "done") && previewUrl && (
              <video
                src={previewUrl}
                muted
                playsInline
                className="h-full w-full object-cover opacity-60"
              />
            )}
            {step === "recording" && (
              <div className="absolute start-3 top-3 flex items-center gap-2 rounded-full bg-red-600 px-3 py-1 text-sm font-medium text-white">
                <span className="recording-dot h-2.5 w-2.5 rounded-full bg-card" />
                {t.identity.recording} {seconds}s / 5s
              </div>
            )}
          </div>
          <canvas ref={canvasRef} className="hidden" />

          {step === "photo" && (
            <Button className="w-full" size="lg" onClick={capturePhoto}>
              <Camera className="h-5 w-5" /> {t.identity.capturePhoto}
            </Button>
          )}
          {step === "recording" && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted">
              <Mic className="h-4 w-4" /> {t.identity.keepTalking}
            </div>
          )}
          {step === "review" && (
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                variant="outline"
                className="flex-1"
                size="lg"
                onClick={retake}
              >
                <RotateCcw className="h-4 w-4" /> {t.identity.retake}
              </Button>
              <Button className="flex-1" size="lg" onClick={confirmAndContinue}>
                <Check className="h-4 w-4" /> {t.identity.continue}
              </Button>
            </div>
          )}
          {(step === "uploading" || step === "done") && (
            <div className="flex items-center justify-center gap-2 text-sm font-medium text-brand-600">
              {step === "uploading" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {step === "uploading" ? t.identity.uploading : t.identity.verified}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
